import React from "react";
import { useHistory } from "react-router-dom";
import { CommandBarButton, Shimmer, useDisclosure, useNotifications } from "sprint-common-web";

import { AuthorizationWrapper } from "../../../../core/security/AuthorizationWrapper";
import { SprintAccessRight } from "../../../../core/security/SprintAccessRight";
import { ciCloudFileListingRoute } from "../../../ContentIngestionRoutes";
import { CloudFileRejectionData } from "../../common/components/reject-cloud-file/CloudFileRejectionData";
import { RejectCloudFileDialogForm } from "../../common/components/reject-cloud-file/RejectCloudFileDialogForm";
import { useRejectCloudFile } from "../../common/components/reject-cloud-file/useRejectCloudFile";
import { isInvalidStatusForReject } from "../../common/types/CloudFileStatus";
import { useCloudFileDetails } from "../common/useCloudFileDetails";

export const RejectCloudFileButton = (): JSX.Element => {
    const { isOpen, onClose, onOpen } = useDisclosure();
    const history = useHistory();
    const { data: cloudFileDetails } = useCloudFileDetails();
    const { success: successNotification, error: errorNotification } = useNotifications();
    const { rejectCloudFile } = useRejectCloudFile({ invalidationKey: cloudFileDetails?.id });

    const reject = async (data: CloudFileRejectionData) => {
        if (!cloudFileDetails) return;

        try {
            await rejectCloudFile.mutateAsync({
                cloudFileId: cloudFileDetails.id,
                reasonId: data.reason.id,
                details: data.details,
                courseOfActionId: data.courseOfAction?.id,
                fixedBillingId: data.fixedBilling?.id
            });

            onClose();

            successNotification("Rejection successful");
            history.push(ciCloudFileListingRoute.path);
        } catch {
            errorNotification("Something went wrong while rejecting the cloud file");
        }
    };

    if (!cloudFileDetails) return <Shimmer />;

    return (
        <AuthorizationWrapper accessRights={[SprintAccessRight.CiAdmin]}>
            <CommandBarButton
                text="Reject"
                icon="PageRemove"
                disabled={isInvalidStatusForReject(cloudFileDetails.status)}
                onClick={onOpen}
                testId="reject-cloud-file-button"
            />
            {isOpen && (
                <RejectCloudFileDialogForm
                    title="Reject cloud file"
                    description="Are you sure you want to reject this cloud file?"
                    onSubmit={reject}
                    onClose={onClose}
                />
            )}
        </AuthorizationWrapper>
    );
};
